import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Search.css';

// Declare Google Maps types
declare global {
  interface Window {
    google?: typeof google;
  }
}

interface SearchProps {
  onAddressSelect: (lat: number, lng: number, displayName: string) => void;
  onSearchStart?: () => void;
  onSearchResultsVisibilityChange?: (visible: boolean) => void;
}

interface SearchResult {
  place_id: string;
  lat: number;
  lng: number;
  display_name: string;
  highlighted_name?: string;
  is_google_result?: boolean;
}

// Define common Toronto addresses for our local database
const commonAddresses = [
  // North York area
  { place_id: 'north-york-1', display_name: '5090 Yonge St, North York, ON', lat: 43.7744, lng: -79.4153 },
  { place_id: 'north-york-2', display_name: '5100 Yonge St, North York, ON', lat: 43.7752, lng: -79.4153 },
  { place_id: 'north-york-3', display_name: '5150 Yonge St, North York, ON', lat: 43.7767, lng: -79.4153 },
  { place_id: 'north-york-4', display_name: '5200 Yonge St, North York, ON', lat: 43.7781, lng: -79.4153 },
  
  // Specific test coordinates that match our parcel-by-point test
  { place_id: 'test-parcel', display_name: 'Test Parcel, North York, ON', lat: 43.77439, lng: -79.52309 },
  
  // Downtown Toronto
  { place_id: 'cn-tower', display_name: '301 Front St W, Toronto, ON', lat: 43.6426, lng: -79.3871 },
  { place_id: 'rogers-centre', display_name: '1 Blue Jays Way, Toronto, ON', lat: 43.6414, lng: -79.3894 },
  { place_id: 'union-station', display_name: '65 Front St W, Toronto, ON', lat: 43.6453, lng: -79.3806 },
  { place_id: 'eaton-centre', display_name: '220 Yonge St, Toronto, ON', lat: 43.6544, lng: -79.3807 },
  { place_id: 'casa-loma', display_name: '1 Austin Terrace, Toronto, ON', lat: 43.6781, lng: -79.4094 },
  { place_id: 'high-park', display_name: '1873 Bloor St W, Toronto, ON', lat: 43.6465, lng: -79.4637 },
  { place_id: 'kensington-market', display_name: '77 Kensington Ave, Toronto, ON', lat: 43.6545, lng: -79.4007 },
  { place_id: 'distillery-district', display_name: '55 Mill St, Toronto, ON', lat: 43.6503, lng: -79.3596 },
  { place_id: 'st-lawrence-market', display_name: '93 Front St E, Toronto, ON', lat: 43.6489, lng: -79.3714 },
  { place_id: 'yorkville', display_name: '55 Avenue Rd, Toronto, ON', lat: 43.6708, lng: -79.3958 }
];

const Search: React.FC<SearchProps> = ({ onAddressSelect, onSearchStart, onSearchResultsVisibilityChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  
  // Refs for new Google Places API
  const placesLibrary = useRef<any>(null);
  

  
  // Debounce timer
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Initialize new Google Places API
  useEffect(() => {

    
    // Function to initialize new Places API
    const initializePlacesAPI = async () => {
      try {
        if (window.google && window.google.maps) {

          
          // Check if importLibrary is available (new API)
          if (typeof (window.google.maps as any).importLibrary === 'function') {
            // Use the new dynamic import method
            const { Place } = await (window.google.maps as any).importLibrary("places");
            placesLibrary.current = { Place };

            return true;
          } else if (window.google.maps.places && (window.google.maps.places as any).Place) {
            // Fallback: Places library already loaded via script tag
            placesLibrary.current = { Place: (window.google.maps.places as any).Place };

            return true;
                  } else {
          console.warn('New Google Places API not available, falling back to local search only');
          return false;
        }
        }
        return false;
      } catch (error) {
        console.error('Error initializing new Google Places API:', error);
        return false;
      }
    };
    
    // Try to initialize immediately if Google Maps is already loaded
    initializePlacesAPI().then((success) => {
      if (success) return;
      
      // Set up retry mechanism if Google Maps isn't loaded yet
      let retryCount = 0;
      const maxRetries = 10;
      const retryInterval = 1000; // 1 second
      
      const retryInitialization = async () => {
        if (retryCount >= maxRetries) {
          console.error('Failed to load Google Maps API after multiple attempts');
          return;
        }
        
        retryCount++;
        console.log(`Retry attempt ${retryCount} to initialize Google Maps API`);
        
        const success = await initializePlacesAPI();
        if (success) {
          console.log('Successfully initialized Google Maps API on retry');
        } else {
          console.log(`Google Maps not available yet, will retry in ${retryInterval}ms`);
          setTimeout(retryInitialization, retryInterval);
        }
      };
      
      // Start retry process
      setTimeout(retryInitialization, retryInterval);
    });
  }, []);

  // Notify parent component when search results visibility changes
  useEffect(() => {
    if (onSearchResultsVisibilityChange) {
      onSearchResultsVisibilityChange(showResults);
    }
  }, [showResults, onSearchResultsVisibilityChange]);

  // Helper function to expand common abbreviations
  const expandCommonAbbreviations = useCallback((text: string): string => {
    const expansions = [
      { regex: /\bave\b/gi, replacement: 'avenue' },
      { regex: /\bblvd\b/gi, replacement: 'boulevard' },
      { regex: /\bst\b/gi, replacement: 'street' },
      { regex: /\brd\b/gi, replacement: 'road' },
      { regex: /\bdr\b/gi, replacement: 'drive' }
    ];
    let expanded = text.toLowerCase();
    expansions.forEach(rule => {
      expanded = expanded.replace(rule.regex, rule.replacement);
    });
    return expanded;
  }, []);

  // Generate highlighted text for search results
  const generateHighlightedText = useCallback((text: string, query: string): string => {
    // Build highlights: only highlight words 2+ characters
    const searchWords = query
      .split(/\s+/)
      .filter(w => w.length > 1)
      .map((w: string) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); // escape special chars

    const highlightRegex =
      searchWords.length > 0 ? new RegExp(`(${searchWords.join('|')})`, 'gi') : null;

    return highlightRegex ? text.replace(highlightRegex, '<strong>$1</strong>') : text;
  }, []);

  // Search function using Google Maps API with local database fallback
  const doSearch = useCallback(async (searchQuery: string) => {
    
    // Clear results for empty queries
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    // Process query to expand abbreviations
    const processedQuery = expandCommonAbbreviations(searchQuery.trim());
    
    // Try new Google Places API first if available
    if (placesLibrary.current?.Place) {
      try {

        
        const { Place } = placesLibrary.current;
        
        // Use the new searchByText method
        const request = {
          textQuery: processedQuery,
          fields: ['id', 'displayName', 'formattedAddress', 'location'],
          locationBias: {
            center: { lat: 43.7, lng: -79.4 },
            radius: 20000 // 20km radius around Toronto
          },
          region: 'ca', // Restrict to Canada
          maxResultCount: 10
        };
        
        const { places } = await Place.searchByText(request);
        
        if (places && places.length > 0) {
          
          // Transform new Places API results to our format
          const googleResults: SearchResult[] = places.map((place: any) => ({
            place_id: place.id,
            lat: place.location?.lat() || 0,
            lng: place.location?.lng() || 0,
            display_name: place.formattedAddress || place.displayName,
            highlighted_name: generateHighlightedText(place.formattedAddress || place.displayName, processedQuery),
            is_google_result: true
          }));
          
          setResults(googleResults);
          setShowResults(true);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('New Google Places API search failed:', error);
        // Fall back to local database
      }
    }
    
    // Fall back to local database if Google API failed or returned no results

    
    // Filter addresses from our local database
    const localResults = commonAddresses.filter(address => 
      address.display_name.toLowerCase().includes(processedQuery.toLowerCase())
    ).map(address => ({
      ...address,
      highlighted_name: generateHighlightedText(address.display_name, processedQuery),
      is_google_result: false
    }));
    

    setResults(localResults);
    setShowResults(localResults.length > 0);
    setIsLoading(false);
  }, [placesLibrary, expandCommonAbbreviations, generateHighlightedText]);

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear any existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    
    // Show loading state immediately for better UX
    if (value.trim().length >= 2) {
      setIsLoading(true);
    }
    
    // Set a new timer to delay the search
    debounceTimer.current = setTimeout(() => {
      doSearch(value);
    }, 300); // 300ms debounce delay
  };
  
  // Handle keyboard navigation in search results
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || results.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowResults(false);
        break;
      default:
        break;
    }
  };

  // Handle result selection
  const handleResultSelect = async (result: SearchResult) => {
    setShowResults(false);
    setQuery(result.display_name); // Update input with selected address
    
    if (onSearchStart) {
      onSearchStart();
    }

    // With the new Places API, coordinates are already available in search results
    onAddressSelect(result.lat, result.lng, result.display_name);
    
    setSelectedIndex(-1); // Reset selection index
  };

  // Clear the input and results
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  // Touch event isolation to prevent unwanted scrolling
  const handleTouchStart = (e: React.TouchEvent) => {
    // Prevent touch events from bubbling to parent containers
    e.stopPropagation();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Prevent touch events from bubbling to parent containers
    e.stopPropagation();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Prevent touch events from bubbling to parent containers
    e.stopPropagation();
  };

  return (
    <div 
      className="search-container"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="search-input-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search for an address in Toronto..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (query.trim().length >= 2) {
              setShowResults(true);
              doSearch(query);
            }
            // Don't call onSearchStart on focus - only on actual selection
          }}
          onBlur={() => {
            setTimeout(() => setShowResults(false), 200);
          }}
          onKeyDown={handleKeyDown}
          aria-label="Search address"
          aria-expanded={showResults}
          aria-autocomplete="list"
          aria-controls="search-results"
          role="combobox"
        />
        
        <div className="search-icon">
          {isLoading ? (
            <div className="search-spinner"></div>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.71 20.29L18 16.61A9 9 0 1 0 16.61 18l3.68 3.68a1 1 0 0 0 1.42 0 1 1 0 0 0 0-1.39zM11 18a7 7 0 1 1 7-7 7 7 0 0 1-7 7z" stroke="currentColor" fill="currentColor"/>
            </svg>
          )}
        </div>
      </div>

      {showResults && (
        <div className="search-results" role="listbox" id="search-results">
          {isLoading ? (
            <div className="search-result-item loading">
              <div className="search-result-spinner"></div>
              <div>Searching addresses...</div>
            </div>
          ) : results.length > 0 ? (
            results.map((result, index) => (
              <div
                key={result.place_id}
                className={`search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => handleResultSelect(result)}
                onMouseEnter={() => setSelectedIndex(index)}
                role="option"
                aria-selected={index === selectedIndex}
                tabIndex={0}
                id={`search-result-${index}`}
              >
                <div className="search-result-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12.75C13.6569 12.75 15 11.4069 15 9.75C15 8.09315 13.6569 6.75 12 6.75C10.3431 6.75 9 8.09315 9 9.75C9 11.4069 10.3431 12.75 12 12.75Z" stroke="currentColor" fill="currentColor" />
                    <path d="M19.5 9.75C19.5 16.5 12 21.75 12 21.75C12 21.75 4.5 16.5 4.5 9.75C4.5 7.76088 5.29018 5.85322 6.6967 4.4467C8.10322 3.04018 10.0109 2.25 12 2.25C13.9891 2.25 15.8968 3.04018 17.3033 4.4467C18.7098 5.85322 19.5 7.76088 19.5 9.75V9.75Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  </svg>
                </div>
                {result.highlighted_name ? (
                  <div className="search-result-text" dangerouslySetInnerHTML={{ __html: result.highlighted_name }} />
                ) : (
                  <div className="search-result-text">{result.display_name}</div>
                )}
              </div>
            ))
          ) : query.length >= 2 ? (
            <div className="search-result-item no-results">
              <div>No locations found</div>
              <div className="search-suggestion">Try a different search term or check the spelling</div>
            </div>
          ) : (
            <div className="search-result-item no-results">
              <div>Type at least 2 characters to search</div>
              <div className="search-suggestion">Try searching for "CN Tower" or "Yonge Street"</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Search;
